import { forwardRef, useCallback, useState } from 'react';
import { Pressable, View } from 'react-native';

import { Avatar, Button, HStack, Text, VStack, theme } from '@squire/ui';

import type { WaitlistTimeSlot } from '@/api/waitlist/waitlistTimeSlotsQuery';
import {
  BottomSheet,
  type BottomSheetRef,
} from '@/components/_shared/BottomSheet';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { DateFormat, formatDateWithTimezone } from '@/utils/dateUtils';

import { styles } from './styles';
import type { WaitlistTimeSlotsSheetProps } from './types';

const WaitlistTimeSlotsSheet = forwardRef<
  BottomSheetRef,
  WaitlistTimeSlotsSheetProps
>(
  (
    {
      timeSlots,
      timezone,
      formattedDate,
      barberName,
      barberPhotoUrl,
      serviceName,
      onConfirm,
      onDismiss,
    },
    ref,
  ) => {
    const { t } = useScreenTranslations('WaitlistTimeSlotsSheet');
    const [selectedSlot, setSelectedSlot] = useState<WaitlistTimeSlot | null>(
      null,
    );

    const handleConfirm = useCallback(() => {
      if (selectedSlot) {
        onConfirm(selectedSlot);
      }
    }, [selectedSlot, onConfirm]);

    return (
      <BottomSheet
        ref={ref}
        enableDynamicSizing
        onDismiss={onDismiss}
        testID="waitlist-time-slots-sheet"
      >
        <VStack style={styles.content}>
          <Text variant="2xlStrong" style={styles.title}>
            {t('title', { date: formattedDate })}
          </Text>

          <HStack space={theme.spacing[3]} style={styles.barberInfo}>
            <Avatar
              uri={barberPhotoUrl}
              size="md"
              shape="square"
              fallbackText={barberName}
            />
            <VStack>
              <Text variant="xlStrong" color="textPrimary">
                {barberName}
              </Text>
              <Text variant="l" color="textSecondary">
                {serviceName}
              </Text>
            </VStack>
          </HStack>

          <View style={styles.grid} testID="time-slots-grid">
            {timeSlots.map((slot) => {
              const isSelected =
                selectedSlot?.waitingListSlotId === slot.waitingListSlotId;
              const formattedTime = formatDateWithTimezone(
                slot.time,
                DateFormat.TIME_ONLY,
                timezone,
              );

              return (
                <Pressable
                  key={slot.waitingListSlotId}
                  testID={`time-slot-${slot.waitingListSlotId}`}
                  onPress={() => setSelectedSlot(slot)}
                  style={[
                    styles.slotButton,
                    isSelected && styles.slotButtonSelected,
                  ]}
                >
                  <Text
                    variant="mStrong"
                    color={isSelected ? 'textOnSelected' : 'textPrimary'}
                  >
                    {formattedTime}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <Button
            testID="confirm-button"
            onPress={handleConfirm}
            disabled={!selectedSlot}
            style={styles.confirmButton}
          >
            {t('confirm')}
          </Button>
        </VStack>
      </BottomSheet>
    );
  },
);

WaitlistTimeSlotsSheet.displayName = 'WaitlistTimeSlotsSheet';

export { WaitlistTimeSlotsSheet };
